import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, HandHeart, HeartHandshake } from "lucide-react";
import { useSite } from "@/context/SiteContext";

const NAV = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
  { name: "Announcements", path: "/announcements" },
  { name: "Outreach & Programs", path: "/programs" },
  { name: "Events", path: "/events" },
  { name: "Ministry in Action", path: "/gallery" },
  { name: "Request Prayer", path: "/request-prayer" },
  { name: "Contact", path: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { site } = useSite();
  const logo = site?.site_settings?.logo_url || "/logo.png";
  const loc = useLocation();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/95 border-b border-slate-200/80 shadow-sm" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
            <img src={logo} alt="ItsAllAboutJesus Outreach and Help Center logo" decoding="async" fetchpriority="high" className="h-11 lg:h-14 w-auto" />
            <span className="hidden sm:block font-head font-bold text-teal leading-tight text-sm lg:text-base max-w-[160px]">
              ItsAllAboutJesus <span className="text-brand-cyan">Outreach</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((n) => (
              <NavLink key={n.path} to={n.path} end={n.path === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "text-teal bg-teal/5" : "text-slate-600 hover:text-teal hover:bg-slate-50"
                  }`}
                data-testid={`nav-${n.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {n.name}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-2">
            <Link to="/request-help" className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white font-semibold py-2.5 px-4 rounded-full transition-all shadow-md hover:shadow-lg text-sm" data-testid="header-need-help">
              <HandHeart className="w-4 h-4" /> I Need Help
            </Link>
            <Link to="/get-involved" className="inline-flex items-center gap-2 bg-brand-terracotta hover:bg-[#D0694E] text-white font-semibold py-2.5 px-4 rounded-full transition-all shadow-md hover:shadow-lg text-sm" data-testid="header-want-help">
              <HeartHandshake className="w-4 h-4" /> I Want to Help
            </Link>
          </div>

          <button className="xl:hidden p-2 text-slate-700" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-slate-200 max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="mobile-nav">
          <div className="px-4 py-3 space-y-1">
            {NAV.map((n) => (
              <NavLink key={n.path} to={n.path} end={n.path === "/"} onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2.5 rounded-lg font-medium ${isActive ? "text-teal bg-teal/5" : "text-slate-700"}`}>
                {n.name}
              </NavLink>
            ))}
            <div className="grid grid-cols-2 gap-2 pt-3">
              <Link to="/request-help" onClick={() => setOpen(false)} className="text-center bg-teal text-white font-semibold py-3 rounded-full" data-testid="mobile-need-help">I Need Help</Link>
              <Link to="/get-involved" onClick={() => setOpen(false)} className="text-center bg-brand-terracotta text-white font-semibold py-3 rounded-full" data-testid="mobile-want-help">I Want to Help</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
